import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkle, PaperPlaneRight, MagnifyingGlass, FlowArrow, Play, Trash,
  CheckCircle, XCircle, User as UserIcon, Robot, ArrowClockwise,
} from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MarkdownMessage from "@/components/MarkdownMessage";

function toolIcon(name) {
  if (name === "search_knowledge_base") return MagnifyingGlass;
  if (name === "list_workflows") return FlowArrow;
  if (name === "run_workflow") return Play;
  return Sparkle;
}

function toolLabel(name) {
  if (name === "search_knowledge_base") return "Searched Knowledge Base";
  if (name === "list_workflows") return "Listed workflows";
  if (name === "run_workflow") return "Ran workflow";
  return name;
}

function ToolBadge({ tu }) {
  const Icon = toolIcon(tu.name);
  const success = tu.status ? tu.status === "success" : true;
  return (
    <div
      data-testid={`assistant-tool-badge-${tu.name}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] mr-2 mb-1 ${
        tu.name === "run_workflow"
          ? success
            ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
            : "border-red-400/30 bg-red-400/10 text-red-300"
          : "border-white/10 bg-graphite-950 text-ink-secondary"
      }`}
    >
      <Icon weight={tu.name === "run_workflow" ? "fill" : "duotone"} className="h-3 w-3" />
      <span>{toolLabel(tu.name)}</span>
      {tu.summary && <span className="opacity-70">· {tu.summary}</span>}
    </div>
  );
}

export default function Assistant() {
  const { user, currentOrg } = useAuth();
  const [messages, setMessages] = useState([]);
  const [pendingProposal, setPendingProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [confirmingRun, setConfirmingRun] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Accept a `?prefill=...` param from other pages (e.g. Lead Recovery's
  // "Draft re-engagement email" action). Populates the chat input once, then
  // clears the param so a refresh doesn't re-inject. User can edit before send.
  useEffect(() => {
    const prefill = searchParams.get("prefill");
    if (prefill) {
      setInput(prefill);
      // Clear the param without leaving a history entry
      const next = new URLSearchParams(searchParams);
      next.delete("prefill");
      setSearchParams(next, { replace: true });
      // Focus the textarea so the user can edit / send immediately
      setTimeout(() => {
        try { inputRef.current?.focus(); } catch { /* ignore */ }
      }, 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assistant/conversation");
      setMessages(data.messages || []);
      setPendingProposal(data.pending_proposal || null);
    } catch {
      toast.error("Could not load conversation");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming, pendingProposal]);

  const handleClear = async () => {
    setShowClear(false);
    try {
      await api.post("/assistant/clear");
      setMessages([]);
      setPendingProposal(null);
      toast.success("Conversation cleared");
    } catch {
      toast.error("Could not clear conversation");
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q || streaming) return;
    const userMsg = { id: `u_${Date.now()}`, role: "user", content: q };
    const assistantMsg = { id: `a_${Date.now()}`, role: "assistant", content: "", tool_uses: [], proposal: null };
    setMessages((m) => [...m, userMsg, assistantMsg]);
    setInput("");
    setPendingProposal(null);
    setStreaming(true);

    try {
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q }),
      });
      if (!res.ok || !res.body) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        if (done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const evt = JSON.parse(payload);
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              if (!last || last.role !== "assistant") return copy;
              if (evt.type === "text") {
                copy[copy.length - 1] = { ...last, content: (last.content || "") + evt.content };
              } else if (evt.type === "tool_use") {
                copy[copy.length - 1] = {
                  ...last,
                  tool_uses: [...(last.tool_uses || []), { name: evt.name, arguments: evt.arguments, summary: "running…" }],
                };
              } else if (evt.type === "tool_result") {
                const tu = [...(last.tool_uses || [])];
                for (let i = tu.length - 1; i >= 0; i--) {
                  if (tu[i].name === evt.name && tu[i].summary === "running…") {
                    tu[i] = { ...tu[i], summary: evt.summary };
                    break;
                  }
                }
                copy[copy.length - 1] = { ...last, tool_uses: tu };
              } else if (evt.type === "proposal") {
                copy[copy.length - 1] = { ...last, proposal: evt.proposal };
              } else if (evt.type === "error") {
                copy[copy.length - 1] = {
                  ...last,
                  content: (last.content || "") + `\n${evt.message}`,
                };
              }
              return copy;
            });
            if (evt.type === "proposal") setPendingProposal(evt.proposal);
          } catch {
            /* ignore malformed frame */
          }
        }
      }
    } catch {
      toast.error("Assistant is unavailable right now");
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { ...last, content: "Something went wrong. Please try again." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const confirmRun = async () => {
    if (!pendingProposal || confirmingRun) return;
    setConfirmOpen(false);
    setConfirmingRun(true);
    try {
      const { data } = await api.post("/assistant/confirm-run", {
        workflow_id: pendingProposal.workflow_id,
      });
      const status = data?.run?.status;
      // Append a synthetic assistant message locally so we don't need to reload
      setMessages((m) => [
        ...m,
        {
          id: `a_${Date.now()}`,
          role: "assistant",
          content: data.message_text,
          tool_uses: [{
            name: "run_workflow",
            arguments: { workflow_id: pendingProposal.workflow_id, workflow_name: pendingProposal.workflow_name },
            summary: status === "success"
              ? `HTTP ${data.run.http_status} in ${data.run.duration_ms}ms`
              : (data.run.error_message || "failed"),
            status,
          }],
        },
      ]);
      if (status === "success") toast.success(`Triggered "${pendingProposal.workflow_name}"`);
      else toast.error(`Trigger failed: ${data?.run?.error_message || "unknown error"}`);
      setPendingProposal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not run workflow");
    } finally {
      setConfirmingRun(false);
    }
  };

  const cancelProposal = async () => {
    if (!pendingProposal) return;
    try {
      await api.post("/assistant/cancel-proposal");
    } catch {
      /* non-blocking */
    }
    setMessages((m) => [
      ...m,
      {
        id: `a_${Date.now()}`,
        role: "assistant",
        content: "Cancelled. I won't run that workflow.",
        tool_uses: [],
      },
    ]);
    setPendingProposal(null);
    toast.success("Proposal cancelled");
  };

  return (
    <div className="p-8 md:p-10 max-w-[1100px] mx-auto" data-testid="assistant-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
            {currentOrg?.name || "Workspace"}
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">AI Assistant</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Ask about your docs, your workflows, or trigger an automation. I&rsquo;ll always confirm before firing anything.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="assistant-refresh"
            onClick={load}
            title="Reload conversation"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
          >
            <ArrowClockwise weight="bold" className="h-4 w-4" />
          </button>
          <button
            data-testid="assistant-clear-button"
            onClick={() => setShowClear(true)}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
          >
            <Trash weight="bold" className="h-4 w-4" />
            Clear conversation
          </button>
        </div>
      </div>

      <div className="mt-8 rounded-md border border-white/10 bg-graphite-900 flex flex-col min-h-[600px]" data-testid="assistant-chat-container">
        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="assistant-messages">
          {loading ? (
            <div className="text-center text-sm text-ink-secondary py-12">Loading conversation…</div>
          ) : messages.length === 0 ? (
            <div className="h-full min-h-[400px] grid place-items-center text-center">
              <div className="max-w-md">
                <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
                  <Robot weight="duotone" className="h-5 w-5 text-ink-secondary" />
                </div>
                <div className="mt-4 font-display text-2xl tracking-tight">How can I help?</div>
                <p className="mt-2 text-sm text-ink-secondary">
                  Try &ldquo;List my workflows&rdquo;, &ldquo;What&rsquo;s in our pricing doc?&rdquo;, or &ldquo;Run the onboarding workflow&rdquo;.
                </p>
              </div>
            </div>
          ) : (
            messages.map((m, idx) => {
              const isUser = m.role === "user";
              return (
                <div key={m.id || idx} className={`flex ${isUser ? "justify-end" : "justify-start"} gap-3`} data-testid={`assistant-message-${m.role}-${idx}`}>
                  {!isUser && (
                    <div className="h-8 w-8 shrink-0 rounded-sm bg-graphite-800 border border-white/10 grid place-items-center">
                      <Sparkle weight="duotone" className="h-4 w-4 text-ink-primary" />
                    </div>
                  )}
                  <div className={`max-w-[80%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
                    {!isUser && m.tool_uses && m.tool_uses.length > 0 && (
                      <div className="mb-2 flex flex-wrap" data-testid={`assistant-tool-uses-${idx}`}>
                        {m.tool_uses.map((tu, i) => <ToolBadge key={i} tu={tu} />)}
                      </div>
                    )}
                    {(m.content || (streaming && !isUser && idx === messages.length - 1)) && (
                      <div
                        data-testid={`assistant-message-bubble-${idx}`}
                        className={`rounded-md px-4 py-3 text-sm leading-relaxed ${
                          isUser
                            ? "bg-white text-graphite-950 whitespace-pre-wrap"
                            : "bg-graphite-800 border border-white/10 text-ink-primary"
                        }`}
                      >
                        {m.content ? (
                          isUser ? (
                            m.content
                          ) : (
                            <MarkdownMessage>{m.content}</MarkdownMessage>
                          )
                        ) : (
                          <span className="inline-flex gap-1 items-center text-ink-secondary">
                            <span className="h-1.5 w-1.5 rounded-full bg-ink-secondary animate-pulse" />
                            <span className="h-1.5 w-1.5 rounded-full bg-ink-secondary animate-pulse [animation-delay:120ms]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-ink-secondary animate-pulse [animation-delay:240ms]" />
                          </span>
                        )}
                      </div>
                    )}
                    {!isUser && m.proposal && idx === messages.length - 1 && pendingProposal && (
                      <div
                        data-testid="assistant-proposal-card"
                        className="mt-3 w-full rounded-md border border-amber-400/25 bg-amber-400/5 p-4"
                      >
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-300">
                          <Play weight="fill" className="h-3 w-3" />
                          Confirm to run workflow
                        </div>
                        <div className="mt-2 font-display text-lg tracking-tight text-ink-primary" data-testid="assistant-proposal-name">
                          {pendingProposal.workflow_name}
                        </div>
                        <div className="mt-1 text-sm text-ink-secondary" data-testid="assistant-proposal-reason">
                          {pendingProposal.reason}
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                          <button
                            data-testid="assistant-proposal-confirm"
                            onClick={() => setConfirmOpen(true)}
                            disabled={confirmingRun}
                            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60 transition-colors"
                          >
                            <CheckCircle weight="fill" className="h-4 w-4" />
                            {confirmingRun ? "Running…" : "Confirm & run"}
                          </button>
                          <button
                            data-testid="assistant-proposal-cancel"
                            onClick={cancelProposal}
                            disabled={confirmingRun}
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
                          >
                            <XCircle weight="bold" className="h-4 w-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {isUser && (
                    <div className="h-8 w-8 shrink-0 rounded-full bg-graphite-800 border border-white/10 grid place-items-center overflow-hidden">
                      {user?.picture
                        ? <img src={user.picture} alt="" className="h-full w-full object-cover" />
                        : <UserIcon weight="duotone" className="h-4 w-4 text-ink-primary" />}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t border-white/10 p-4 flex items-center gap-3">
          <input
            data-testid="assistant-input"
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={streaming || !!pendingProposal}
            placeholder={pendingProposal ? "Confirm or cancel the pending action first…" : "Ask about docs, workflows, or say “run the …” to trigger one"}
            className="flex-1 bg-graphite-800 border border-white/10 rounded-md px-4 py-2.5 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-colors disabled:opacity-60"
          />
          <button
            data-testid="assistant-send"
            type="submit"
            disabled={streaming || !input.trim() || !!pendingProposal}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2.5 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
          >
            <PaperPlaneRight weight="bold" className="h-4 w-4" />
            {streaming ? "Thinking…" : "Send"}
          </button>
        </form>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="border-white/10 bg-graphite-800 text-ink-primary" data-testid="assistant-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-light tracking-tight text-2xl">
              Run &ldquo;{pendingProposal?.workflow_name}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary text-sm">
              This will POST to the workflow&rsquo;s webhook and be logged in your Run log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="assistant-confirm-cancel"
              className="border-white/10 bg-transparent hover:bg-white/5 text-ink-primary"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="assistant-confirm-run"
              onClick={confirmRun}
              className="bg-white text-graphite-950 hover:bg-neutral-200"
            >
              Confirm &amp; run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showClear} onOpenChange={setShowClear}>
        <AlertDialogContent className="border-white/10 bg-graphite-800 text-ink-primary" data-testid="assistant-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-light tracking-tight text-2xl">Clear conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary text-sm">
              Your current thread will be erased. This won&rsquo;t affect your documents or workflows.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="assistant-clear-cancel"
              className="border-white/10 bg-transparent hover:bg-white/5 text-ink-primary"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="assistant-clear-confirm"
              onClick={handleClear}
              className="bg-accent-danger text-graphite-950 hover:bg-red-300"
            >
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
